import React from 'react';
import { Link, NavLink, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { alpha, makeStyles } from '@material-ui/core/styles';
import {
  AppBar,
  Menu,
  MenuItem,
  IconButton,
  Toolbar,
  Typography,
  Container,
  Divider,
  Button,
} from '@material-ui/core';
import MoreIcon from '@material-ui/icons/MoreVert';
import { decodeToken } from '../../utils/index';
import BrdLogo from '../../assets/brd-logo-color.svg';
import ToggleDark from './ToggleDark';
import Language from './Language';
import Logout from './Logout';
import Notifications from 'components/notifications/notification';

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },

  title: {
    fontSize: '2em',
    color: '#8DC63F',
    fontFamily: 'Poppins',
    fontWeight: 'bold',
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(3),
      width: 'auto',
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: '100%',
    // position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
  sectionDesktop: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  sectionMobile: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  sectionLink: {
    fontSize: '1.0rem',
    margin: 7,
    padding: '10px 20px',
    color: '#198754',
    textDecoration: 'none',
    '&:hover': {
      background: '#198754',
      color: '#fff',
      'border-radius': '3px',
    },
  },
  sectionLinkMobile: {
    color: '#198754',
    textDecoration: 'none',
    fontWeight: 'bold',
  },
  submit: {
    margin: theme.spacing(0, 0, 0, 2),
    backgroundColor: '#198754',
    fontFamily: 'Poppins',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#198754',
      color: '#fff',
      opacity: 0.8,
    },
  },
}));

const Nav = ({ darkMode, setDarkMode }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
      <MenuItem onClick={handleMenuClose}>My account</MenuItem>
    </Menu>
  );

  const isLoggedIn = decodeToken() !== undefined && decodeToken().id;
  const data = decodeToken() !== undefined && decodeToken();

  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={handleMenuClose}>
        <Link to='/' className={classes.sectionLinkMobile}>
          {t('home')}
        </Link>
      </MenuItem>
      <MenuItem onClick={handleMenuClose}>
        <Link to='/about' className={classes.sectionLinkMobile}>
          {t('about')}
        </Link>
      </MenuItem>
      {/* <MenuItem onClick={handleMenuClose}>
				<Link to='/contact' className={classes.sectionLinkMobile}>
					{t('contact')}
				</Link>
			</MenuItem> */}
      <MenuItem onClick={handleMenuClose}>
        {!isLoggedIn ? (
          <Link to='/auth' className={`${classes.sectionLinkMobile} link-btn`}>
            {t('login')}
          </Link>
        ) : (
          <Link to='/auth' className={`${classes.sectionLinkMobile}`}>
            <Logout />
          </Link>
        )}
      </MenuItem>
    </Menu>
  );

  return (
    <div className={classes.grow}>
      <AppBar
        position='static'
        style={{ backgroundColor: 'white', color: '#198754' }}
      >
        {/* <Container> */}
        <Toolbar>
          <Link to='/'>
            <img src={BrdLogo} alt='BRD logo' width={50} />
          </Link>
          <Link to='/' style={{ textDecoration: 'none' }}>
            <Typography className={classes.title} variant='h6' noWrap>
              {t('title')}
            </Typography>
          </Link>
          <div className={classes.grow} />
          <div className={classes.sectionDesktop}>
            {isLoggedIn ? (
              <>
                {data.applicant === null && data.isSuperAdmin ? (
                  <>
                    {/* <NavLink
											to='/dashboard'
											className={classes.sectionLink}
											activeClassName='active'
										>
											{t('dashboard')}
										</NavLink> */}
                    <NavLink
                      to='/users'
                      className={classes.sectionLink}
                      activeClassName='active'
                    >
                      {t('users')}
                    </NavLink>
                    <NavLink
                      to='/applications'
                      className={classes.sectionLink}
                      activeClassName='active'
                    >
                      {t('application')}
                    </NavLink>
                  </>
                ) : data?.userRole?.RoleName === 'Bank Officer' ||
                  data?.userRole?.RoleName === 'Back-End Officer' ||
                  data?.userRole?.RoleName === 'Admin' ? (
                  <>
                    {data?.userRole?.RoleName === 'Back-End Officer' && (
                      <NavLink
                        to='/users'
                        className={classes.sectionLink}
                        activeClassName='active'
                      >
                        {t('users')}
                      </NavLink>
                    )}
                    {data?.userRole?.RoleName === 'Bank Officer' && (
                      <NavLink
                        to='/bank'
                        className={classes.sectionLink}
                        activeClassName='active'
                      >
                        {t('application')}
                      </NavLink>
                    )}

                    {data?.userRole?.RoleName !== 'Bank Officer' && (
                      <>
                        <NavLink
                          to='/loan/applications'
                          className={classes.sectionLink}
                          activeClassName='active'
                        >
                          {t('application')}
                        </NavLink>
                        <Notifications />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <NavLink
                      to='/my/application'
                      className={classes.sectionLink}
                      activeClassName='active'
                    >
                      {t('my-application')}
                    </NavLink>
                    <Notifications />
                  </>
                )}
                {/* {data.isSuperAdmin && (
									<NavLink
										to='/application'
										className={classes.sectionLink}
										activeClassName='active'
									>
										Dashboard
									</NavLink>
								)} */}
              </>
            ) : (
              <>
                <NavLink
                  to='/'
                  exact
                  className={classes.sectionLink}
                  activeClassName='active'
                >
                  {t('home')}
                </NavLink>
                <NavLink
                  to='about'
                  className={classes.sectionLink}
                  activeClassName='active'
                >
                  {t('about')}
                </NavLink>
                {/* <NavLink
									to='contact'
									className={classes.sectionLink}
									activeClassName='active'
								>
									{t('contact')}
								</NavLink> */}
              </>
            )}
            <div
              style={{
                border: '1px solid #DADADA',
                marginLeft: 15,
                marginRight: 15,
              }}
            ></div>
            
            <Language darkMode={darkMode} setDarkMode={setDarkMode} />
            <div className='nav-btn'>
              {!isLoggedIn && (
                <Button
                  variant='contained'
                  size='large'
                  className='custom-btn'
                  onClick={() => history.push('/auth', { tab: 1 })}
                >
                  {t('login')}
                </Button>
              )}
            </div>
          </div>
          <div className={classes.sectionMobile}>
            <Language />
            <IconButton
              aria-label='show more'
              aria-controls={mobileMenuId}
              aria-haspopup='true'
              onClick={handleMobileMenuOpen}
              color='inherit'
            >
              <MoreIcon />
            </IconButton>
          </div>
        </Toolbar>
        {/* </Container> */}
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
    </div>
  );
};

export default Nav;
